import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../../stores/authStore'
import { handleMockRoute } from './mockData'
import toast from 'react-hot-toast'

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://kaseb-backend.amihermanm.workers.dev/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState()
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle token refresh and fallback to mock
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Handle 401 - try refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshed = await useAuthStore.getState().refreshAccessToken()

      if (refreshed) {
        // Retry original request with new token
        const { accessToken } = useAuthStore.getState()
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }
        return api(originalRequest)
      }
    }

    // Fallback to local mock storage on network, auth, or server errors
    if (!error.response || error.response.status >= 400 || error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      const url = originalRequest?.url || ''
      const method = (originalRequest?.method || 'GET').toUpperCase()
      let body: any = undefined
      if (originalRequest?.data) {
        try {
          body = typeof originalRequest.data === 'string' ? JSON.parse(originalRequest.data) : originalRequest.data
        } catch {
          body = originalRequest.data
        }
      }
      const mockResult = handleMockRoute(method, url, body)
      return {
        data: mockResult,
        status: 200,
        statusText: 'OK (Local DB)',
        headers: {},
        config: originalRequest
      } as any
    }

    // Handle other errors
    const message = (error.response?.data as any)?.detail || error.message

    if (error.response?.status !== 401 && error.response?.status !== 422) {
      toast.error(message || 'خطایی رخ داده است')
    }

    return Promise.reject(error)
  }
)

// API helper functions with automatic local fallback
export const apiGet = <T>(url: string, params?: Record<string, any>): Promise<T> =>
  api.get<T>(url, { params }).then(res => res.data).catch(() => handleMockRoute('GET', url, params) as T)

export const apiPost = <T>(url: string, data?: any): Promise<T> =>
  api.post<T>(url, data).then(res => res.data).catch(() => handleMockRoute('POST', url, data) as T)

export const apiPatch = <T>(url: string, data?: any): Promise<T> =>
  api.patch<T>(url, data).then(res => res.data).catch(() => handleMockRoute('PATCH', url, data) as T)

export const apiPut = <T>(url: string, data?: any): Promise<T> =>
  api.put<T>(url, data).then(res => res.data).catch(() => handleMockRoute('PUT', url, data) as T)

export const apiDelete = <T>(url: string): Promise<T> =>
  api.delete<T>(url).then(res => res.data).catch(() => handleMockRoute('DELETE', url) as T)

// Upload helper
export const apiUpload = <T>(url: string, file: File, onProgress?: (progress: number) => void) => {
  const formData = new FormData()
  formData.append('file', file)

  return api.post<T>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    },
  }).then(res => res.data)
}

// Download helper
export const apiDownload = (url: string, filename?: string) =>
  api.get(url, { responseType: 'blob' }).then(res => {
    const blob = new Blob([res.data])
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  })

// Batch requests
export const apiBatch = <T>(requests: Array<{ method: string; url: string; data?: any }>) =>
  api.post<T>('/batch', { requests }).then(res => res.data)

export default api